import React from 'react';
import { Card, Typography, Progress, Chip } from "@material-tailwind/react";
import { FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiActivity, FiServer, FiCpu, FiHardDrive } from 'react-icons/fi';

const QueueStatistics = ({ queueStats, formatTimestamp }) => {
    if (!queueStats) return null;

    const getJobStatusIcon = (state) => {
        switch (state) {
            case 'pending':
            case 'created':
                return <FiClock className="w-4 h-4" />;
            case 'processing':
            case 'active':
                return <FiActivity className="w-4 h-4 animate-pulse" />;
            case 'completed':
                return <FiCheckCircle className="w-4 h-4" />;
            case 'failed':
                return <FiXCircle className="w-4 h-4" />;
            case 'cancelled':
                return <FiAlertCircle className="w-4 h-4" />;
            default:
                return <FiClock className="w-4 h-4" />;
        }
    };

    const getJobStatusColor = (state) => {
        switch (state) {
            case 'pending':
            case 'created':
                return 'blue';
            case 'processing':
            case 'active':
                return 'orange';
            case 'completed':
                return 'green';
            case 'failed':
                return 'red';
            case 'cancelled':
                return 'gray';
            default:
                return 'blue-gray';
        }
    };

    // Calculate totals and metrics
    const totalJobs = Object.values(queueStats.jobCounts || {}).reduce((sum, count) => sum + count, 0);
    const completedJobs = queueStats.jobCounts?.completed || 0;
    const successRate = totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0;

    // Calculate average processing time from performance data
    const avgProcessingTime = queueStats.performance?.length > 0
        ? queueStats.performance.reduce((sum, perf) => sum + parseFloat(perf.avg_duration_seconds || 0), 0) / queueStats.performance.length
        : 0;

    // Get most recent job
    const mostRecentJob = queueStats.recentJobs?.[0];

    // Memory usage percentage (assuming 512MB as max based on queue.js)
    const memoryUsedMB = queueStats.memoryStats?.memoryUsage?.heapUsed ?
        Math.round(queueStats.memoryStats.memoryUsage.heapUsed / 1024 / 1024) : 0;
    const memoryPercentage = Math.min((memoryUsedMB / 512) * 100, 100);

    return (
        <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Jobs */}
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                Total Jobs
                            </Typography>
                            <Typography variant="h4" color="blue-gray" className="font-bold">
                                {totalJobs}
                            </Typography>
                        </div>
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <FiServer className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                </Card>

                {/* Success Rate */}
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                Success Rate
                            </Typography>
                            <Typography variant="h4" color={successRate >= 90 ? "green" : successRate >= 70 ? "orange" : "red"} className="font-bold">
                                {successRate}%
                            </Typography>
                        </div>
                        <div className={`p-2 rounded-lg ${successRate >= 90 ? 'bg-green-50' : successRate >= 70 ? 'bg-orange-50' : 'bg-red-50'}`}>
                            <FiCheckCircle className={`w-6 h-6 ${successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-orange-600' : 'text-red-600'}`} />
                        </div>
                    </div>
                </Card>

                {/* Average Processing Time */}
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                Avg. Processing
                            </Typography>
                            <Typography variant="h4" color="blue-gray" className="font-bold">
                                {avgProcessingTime.toFixed(2)}s
                            </Typography>
                        </div>
                        <div className="p-2 bg-purple-50 rounded-lg">
                            <FiClock className="w-6 h-6 text-purple-600" />
                        </div>
                    </div>
                </Card>

                {/* Currently Processing */}
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                Active Jobs
                            </Typography>
                            <Typography variant="h4" color={queueStats.currentlyProcessing > 0 ? "orange" : "green"} className="font-bold">
                                {queueStats.currentlyProcessing}
                            </Typography>
                        </div>
                        <div className={`p-2 rounded-lg ${queueStats.currentlyProcessing > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                            <FiActivity className={`w-6 h-6 ${queueStats.currentlyProcessing > 0 ? 'text-orange-600 animate-pulse' : 'text-green-600'}`} />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Status Breakdown */}
            <Card className="p-6">
                <Typography variant="h6" color="blue-gray" className="mb-4">
                    Job Status Breakdown
                </Typography>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {queueStats.jobCounts && Object.entries(queueStats.jobCounts).map(([status, count]) => (
                        <div key={status} className="text-center">
                            <div className="flex items-center justify-center mb-2">
                                {getJobStatusIcon(status)}
                            </div>
                            <Typography variant="h4" color="blue-gray" className="font-bold">
                                {count}
                            </Typography>
                            <Chip
                                size="sm"
                                variant="filled"
                                color={getJobStatusColor(status)}
                                value={status}
                                className="capitalize mt-1"
                            />
                        </div>
                    ))}
                </div>
            </Card>

            {/* System Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Memory & System Stats */}
                <Card className="p-6">
                    <Typography variant="h6" color="blue-gray" className="mb-4">
                        System Performance
                    </Typography>
                    <div className="space-y-4">
                        {/* Uptime */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FiServer className="w-4 h-4" />
                                <Typography variant="small" color="blue-gray">Uptime</Typography>
                            </div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                {queueStats.memoryStats?.uptimeFormatted || 'N/A'}
                            </Typography>
                        </div>

                        {/* Memory Usage */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <FiHardDrive className="w-4 h-4" />
                                    <Typography variant="small" color="blue-gray">Memory Usage</Typography>
                                </div>
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    {queueStats.memoryStats?.memoryUsageFormatted?.heapUsed || 'N/A'}
                                </Typography>
                            </div>
                            <Progress
                                value={memoryPercentage}
                                color={memoryPercentage > 80 ? "red" : memoryPercentage > 60 ? "orange" : "green"}
                                className="h-2"
                            />
                        </div>

                        {/* Workers */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FiCpu className="w-4 h-4" />
                                <Typography variant="small" color="blue-gray">Active Workers</Typography>
                            </div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                {queueStats.memoryStats?.totalWorkers || 0}
                            </Typography>
                        </div>

                        {/* Max Concurrency */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FiActivity className="w-4 h-4" />
                                <Typography variant="small" color="blue-gray">Max Concurrency</Typography>
                            </div>
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                {queueStats.memoryStats?.maxConcurrency || 0}
                            </Typography>
                        </div>
                    </div>
                </Card>

                {/* Job Performance by Type */}
                <Card className="p-6">
                    <Typography variant="h6" color="blue-gray" className="mb-4">
                        Job Performance by Type
                    </Typography>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                        {queueStats.performance?.map((perf, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                        {perf.name}
                                    </Typography>
                                    <Chip
                                        size="sm"
                                        variant="filled"
                                        color={parseFloat(perf.failed || 0) === 0 ? "green" : "red"}
                                        value={`${Math.round((parseFloat(perf.completed || 0) / parseFloat(perf.total_jobs || 1)) * 100)}%`}
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div className="text-center">
                                        <Typography variant="small" color="blue-gray" className="font-bold">
                                            {perf.total_jobs}
                                        </Typography>
                                        <Typography variant="small" color="gray">
                                            Total
                                        </Typography>
                                    </div>
                                    <div className="text-center">
                                        <Typography variant="small" color="blue-gray" className="font-bold">
                                            {parseFloat(perf.avg_duration_seconds || 0).toFixed(2)}s
                                        </Typography>
                                        <Typography variant="small" color="gray">
                                            Avg Time
                                        </Typography>
                                    </div>
                                    <div className="text-center">
                                        <Typography variant="small" color="blue-gray" className="font-bold">
                                            {parseFloat(perf.max_duration_seconds || 0).toFixed(2)}s
                                        </Typography>
                                        <Typography variant="small" color="gray">
                                            Max Time
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        )) || (
                                <Typography variant="small" color="gray" className="text-center italic">
                                    No performance data available
                                </Typography>
                            )}
                    </div>
                </Card>
            </div>

            {/* Recent Activity */}
            <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <Typography variant="h6" color="blue-gray">
                        Recent Jobs
                    </Typography>
                    {mostRecentJob && (
                        <Typography variant="small" color="gray">
                            Last: {formatTimestamp(mostRecentJob.created_at)}
                        </Typography>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr className="border-b border-blue-gray-100">
                                <th className="p-2">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        ID
                                    </Typography>
                                </th>
                                <th className="p-2">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Name
                                    </Typography>
                                </th>
                                <th className="p-2">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Status
                                    </Typography>
                                </th>
                                <th className="p-2">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Duration
                                    </Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {queueStats.recentJobs?.slice(0, 5).map((job, index) => {
                                const duration = job.completed_at && job.started_at
                                    ? ((new Date(job.completed_at) - new Date(job.started_at)) / 1000).toFixed(2)
                                    : 'N/A';

                                return (
                                    <tr key={job.id} className={index % 2 === 0 ? 'bg-blue-gray-50/50' : ''}>
                                        <td className="p-2">
                                            <Typography variant="small" color="blue-gray" className="font-mono">
                                                #{job.id}
                                            </Typography>
                                        </td>
                                        <td className="p-2">
                                            <Typography variant="small" color="blue-gray" className="font-medium">
                                                {job.name}
                                            </Typography>
                                        </td>
                                        <td className="p-2">
                                            <Chip
                                                size="sm"
                                                variant="filled"
                                                color={getJobStatusColor(job.status)}
                                                value={job.status}
                                                className="capitalize"
                                            />
                                        </td>
                                        <td className="p-2">
                                            <Typography variant="small" color="blue-gray">
                                                {duration}s
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            }) || (
                                    <tr>
                                        <td colSpan="4" className="p-4 text-center text-gray-500 italic">
                                            No recent jobs
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Last Updated */}
            {queueStats.timestamp && (
                <div className="text-center">
                    <Typography variant="small" color="gray">
                        Last updated: {formatTimestamp(queueStats.timestamp)}
                    </Typography>
                </div>
            )}
        </div>
    );
};

export default QueueStatistics;
